import styled from 'styled-components';

import { CARD_BORDER, CARD_SHADOW, CONTROL_BORDER, FOCUS_RING } from './base';

// ---------------------------------------------------------------------------
// Régua de Cobrança — drawer do caso: painel de 620px colado à direita com
// cabeçalho escuro, corpo rolável de cards brancos e timeline.
//
// A animação é de deslize lateral nos DOIS sentidos (show/hide), dirigida por
// `$show` via transition — o componente segura a desmontagem até o fim da
// saída (mesma mecânica do Modal compartilhado).
// ---------------------------------------------------------------------------

export const DRAWER_ANIM_MS = 220;

export const DrawerBackdrop = styled.div`
   position: fixed;
   inset: 0;
   background: ${({ theme }) => theme?.colors?.backdrop ?? 'rgba(0, 0, 0, 0.35)'};
   z-index: 1200;
   opacity: ${({ $show }) => ($show ? 1 : 0)};
   transition: opacity ${DRAWER_ANIM_MS}ms ease;

   @media (prefers-reduced-motion: reduce) {
      transition: none;
   }
`;

export const DrawerPanel = styled.aside`
   position: fixed;
   top: 0;
   right: 0;
   bottom: 0;
   width: min(620px, 100vw);
   background: #f7f7f8;
   z-index: 1201;
   display: flex;
   flex-direction: column;
   transform: ${({ $show }) => ($show ? 'none' : 'translateX(105%)')};
   transition: transform ${DRAWER_ANIM_MS}ms ease;

   @media (prefers-reduced-motion: reduce) {
      transition: none;
   }
`;

// ─── Cabeçalho escuro ─────────────────────────────────────────────────────

export const DrawerHead = styled.div`
   background: ${({ theme }) => theme?.colors?.appBackground ?? '#373737'};
   color: #fff;
   padding: 16px 20px;
   display: flex;
   flex-direction: column;
   gap: 12px;
   flex: 0 0 auto;
`;

export const DrawerTitleRow = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 12px;
`;

export const DrawerIdentity = styled.div`
   flex: 1;
   min-width: 0;
`;

export const DrawerName = styled.div`
   font-size: 17px;
   font-weight: 700;
   line-height: 1.2;
`;

export const DrawerDoc = styled.div`
   font-size: 12px;
   color: #bfbfbf;
   margin-top: 3px;
   font-variant-numeric: tabular-nums;
`;

export const DrawerClose = styled.button`
   width: 32px;
   height: 32px;
   flex: 0 0 32px;
   border-radius: 10px;
   border: 1px solid rgba(255, 255, 255, 0.16);
   background: transparent;
   color: #e5e5e5;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition: background 120ms ease;

   &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
   }

   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.24);
   }
`;

export const DrawerStatsRow = styled.div`
   display: flex;
   align-items: flex-end;
   gap: 18px;
   flex-wrap: wrap;
`;

export const DrawerStat = styled.div`
   min-width: 0;
`;

export const DrawerStatLabel = styled.div`
   font-size: 10px;
   font-weight: 800;
   letter-spacing: 0.08em;
   text-transform: uppercase;
   color: #a9a9a9;
`;

export const DrawerStatValue = styled.b`
   font-size: 22px;
   font-weight: 800;
   font-variant-numeric: tabular-nums;
   white-space: nowrap;
`;

export const DrawerStatsSpacer = styled.div`
   flex: 1;
   min-width: 8px;
`;

export const DrawerMetaRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
   font-size: 12px;
   color: #bfbfbf;
   border-top: 1px solid rgba(255, 255, 255, 0.1);
   padding-top: 10px;
`;

export const DrawerMetaDivider = styled.span`
   opacity: 0.35;
`;

// Valor do compromisso no cabeçalho (somente leitura): cores por urgência.
export const CommitValue = styled.b`
   font-weight: 700;
   color: ${({ $tone }) => {
      if ($tone === 'danger') return '#ffb4b4';
      if ($tone === 'warn') return '#ffd591';
      if ($tone === 'muted') return '#bfbfbf';
      return '#fff';
   }};
`;

// ─── Corpo ────────────────────────────────────────────────────────────────

export const DrawerBody = styled.div`
   flex: 1;
   min-height: 0;
   overflow-y: auto;
   padding: 14px;
   display: flex;
   flex-direction: column;
   gap: 14px;
`;

export const DrawerCard = styled.section`
   background: #fff;
   border: 1px solid ${CARD_BORDER};
   border-radius: 12px;
   box-shadow: ${CARD_SHADOW};
   flex: 0 0 auto;
   overflow: hidden;
`;

export const DrawerCardPad = styled(DrawerCard)`
   padding: 14px;
   display: flex;
   flex-direction: column;
   gap: 12px;
   overflow: visible;
`;

// ─── Seções colapsáveis do drawer (2026-08-31) ────────────────────────────
// O drawer virou uma pilha longa (títulos + tratativa + histórico) e o
// histórico, que é o que mais cresce, fica no fim. Poder recolher as duas
// primeiras seções encurta o caminho até ele.

// Cabeçalho de seção: título à esquerda, contador/hint no meio, gatilho na
// ponta direita.
export const SectionHeadRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;

// Gatilho 26×26 com chevron. Rotaciona 180° ao abrir.
// ⚠️ FC7 G.4.1: declara o próprio `:hover` COM background, senão o azul do
// GlobalStyles legado vaza por especificidade (`button:hover` = 0,0,1,1).
export const CollapseToggle = styled.button`
   width: 26px;
   height: 26px;
   flex: 0 0 26px;
   border-radius: 8px;
   border: 1px solid ${CONTROL_BORDER};
   background: #fff;
   color: #666;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-family: inherit;
   transition: background 140ms ease, border-color 140ms ease, color 140ms ease;

   &:hover:not(:disabled) {
      background: #f3f8fe;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      color: #1f66bd;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }

   svg {
      transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
      transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
   }

   @media (prefers-reduced-motion: reduce) {
      svg {
         transition: none;
      }
   }
`;

// Corpo colapsável com altura ANIMADA sem número mágico: o truque é o grid de
// uma linha indo de `0fr` a `1fr` — o próprio conteúdo define o alvo, então
// funciona igual para uma tabela de 1 título ou de 12, sem `max-height`
// chutado (que estraga o easing quando o conteúdo é bem menor que o teto).
//
// O filho precisa de `min-height: 0` + `overflow: hidden` para poder ser
// espremido; sem isso o grid não encolhe abaixo do conteúdo.
export const Collapsible = styled.div`
   display: grid;
   grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
   opacity: ${({ $open }) => ($open ? 1 : 0)};
   transition: grid-template-rows 260ms cubic-bezier(0.2, 0.8, 0.2, 1),
      opacity 180ms ease;

   > * {
      min-height: 0;
      overflow: hidden;
   }

   @media (prefers-reduced-motion: reduce) {
      transition: none;
   }
`;

// ─── Registrar tratativa ──────────────────────────────────────────────────

// Chip-rádio de canal (34px, raio 999). Ativo em azul info.
export const ChannelChip = styled.button`
   height: 34px;
   padding: 0 14px;
   border-radius: 999px;
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
   border: 1px solid
      ${({ $active }) =>
         $active ? 'rgba(64, 152, 239, 0.5)' : CONTROL_BORDER};
   background: ${({ $active }) =>
      $active ? 'rgba(64, 152, 239, 0.12)' : '#fff'};
   color: ${({ $active }) => ($active ? '#0b3a63' : '#666')};

   &:hover:not(:disabled) {
      background: ${({ $active }) =>
         $active ? 'rgba(64, 152, 239, 0.18)' : '#f3f8fe'};
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }
`;

// Botão "+ Novo compromisso" (36px, texto azul de link).
// Gatilho de reatribuição no cabeçalho escuro (decisão 2026-08-17): o
// avatar+nome do responsável vira botão discreto que abre o AssignModal.
// Hover próprio COM background (G.4.1 — sem isso herda o azul do legado).
export const HeadAssignButton = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 7px;
   padding: 3px 8px;
   margin: -3px -8px;
   border: none;
   border-radius: 8px;
   background: transparent;
   color: inherit;
   font: inherit;
   cursor: pointer;
   transition: background 120ms ease, color 120ms ease;

   &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }
`;

// "+ Novo compromisso" no cabeçalho escuro (movido do card de tratativa em
// 2026-08-17): ghost translúcido legível sobre o fundo #373737. Abrir o modal
// não é escrita (G.1) — quem leva o azul é o Confirmar do modal.
export const HeadCommitButton = styled.button`
   height: 28px;
   padding: 0 12px;
   border-radius: 8px;
   border: 1px solid rgba(255, 255, 255, 0.28);
   background: rgba(255, 255, 255, 0.08);
   color: #fff;
   font-weight: 700;
   font-size: 12px;
   font-family: inherit;
   cursor: pointer;
   white-space: nowrap;
   margin-left: auto;
   transition: background 120ms ease, border-color 120ms ease;

   &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.18);
      border-color: rgba(255, 255, 255, 0.45);
   }

   &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }
`;

export const InteractionFoot = styled.div`
   display: flex;
   align-items: flex-end;
   gap: 10px;
   flex-wrap: wrap;
   border-top: 1px solid rgba(0, 0, 0, 0.07);
   padding-top: 12px;
`;

// ─── Timeline ─────────────────────────────────────────────────────────────

export const TimelineItem = styled.div`
   display: flex;
   gap: 12px;
`;

export const TimelineRail = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   flex: 0 0 auto;
`;

// Tom da bolinha por natureza do item (derive.buildTimeline): azul = ação de
// usuário (tratativa), âmbar = mudança de raia/status, verde = recebimento,
// cinza = sistema. Mesma família de cores das pills warn/success do módulo.
const TIMELINE_DOT_COLORS = {
   status: '#f59e0b',
   success: '#16a34a',
   system: '#c9c9c9',
};

export const TimelineDot = styled.span`
   width: 9px;
   height: 9px;
   border-radius: 999px;
   margin-top: 4px;
   flex: 0 0 9px;
   background: ${({ $tone, theme }) =>
      $tone === 'user'
         ? theme?.colors?.primary ?? '#4098ef'
         : TIMELINE_DOT_COLORS[$tone] ?? TIMELINE_DOT_COLORS.system};
`;

export const TimelineLine = styled.span`
   flex: 1;
   width: 1px;
   background: ${({ $last }) => ($last ? 'transparent' : 'rgba(0, 0, 0, 0.1)')};
`;

export const TimelineContent = styled.div`
   padding-bottom: 14px;
   min-width: 0;
   flex: 1;
`;

export const TimelineTitleRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

export const TimelineTitle = styled.b`
   font-size: 12px;
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const TimelineBody = styled.div`
   font-size: 13px;
   color: #333;
   line-height: 1.5;
   margin-top: 4px;
   overflow-wrap: anywhere;
`;

export const TimelineMeta = styled.div`
   font-size: 11px;
   color: #9a9a9a;
   margin-top: 4px;
`;
